import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFirestore, doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL, uploadBytes } from "firebase/storage";
import { cloudRunStartPodcastTranscribe } from "../../../helpers/AI_functions";

const useAutosizeTextarea = (ref, value, { min = 10, max = 260 } = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${next}px`;
  }, [ref, value, min, max]);
};

const safeStringify = (value, fallback) => {
  try {
    return JSON.stringify(value ?? fallback, null, 2);
  } catch {
    return JSON.stringify(fallback, null, 2);
  }
};

const tryParseJson = (text) => {
  try {
    return { ok: true, value: JSON.parse(text), error: "" };
  } catch (e) {
    return { ok: false, value: null, error: "Invalid JSON. Fix formatting and try again." };
  }
};

const JsonDocEditor = ({
  title,
  docRef,
  defaultContent,
  isActive,
  minHeight = 10,
  maxHeight = 260
}) => {
  const [text, setText] = useState(safeStringify(defaultContent, defaultContent));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const taRef = useRef(null);


  useAutosizeTextarea(taRef, text, { min: minHeight, max: maxHeight });

  const parsed = useMemo(() => tryParseJson(text), [text]);

  const load = async () => {
    if (!docRef) return;
    try {
      setLoading(true);
      setError("");
      setStatus("");

      const snap = await getDoc(docRef);
      const content = snap.exists() ? snap.data()?.content : undefined;

      setText(safeStringify(content, defaultContent));
      setStatus("Loaded.");
    } catch (e) {
      console.error(e);
      setError("Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!docRef) return;

    const p = tryParseJson(text);
    if (!p.ok) {
      setError(p.error);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setStatus("");

      await setDoc(docRef, { content: p.value }, { merge: true });

      setStatus("Saved.");
    } catch (e) {
      console.error(e);
      setError("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    load();
  }, [isActive, docRef?.path]);

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontWeight: 800 }}>{title}</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} disabled={loading || !docRef}>
            {loading ? "Loading…" : "Cancel"}
          </button>
          <button onClick={save} disabled={saving || !docRef || !parsed.ok}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight,
            maxHeight,
            overflowY: "auto",
            resize: "none",
            padding: 10,
            borderRadius: 8,
            border: "1px solid #ddd",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre",
            lineHeight: 1.35
          }}
        />
      </div>

      <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center" }}>
        {error && <div style={{ color: "crimson", fontWeight: 700 }}>{error}</div>}
        {!error && !parsed.ok && (
          <div style={{ color: "crimson", fontWeight: 700 }}>{parsed.error}</div>
        )}
        {!error && parsed.ok && status && (
          <div style={{ color: "#065f46", fontWeight: 700 }}>{status}</div>
        )}
      </div>
    </div>
  );
};

const SentencesJsonViewer = ({ isActive, storage, podcastId, langCode, nodeIdStr }) => {
  const [text, setText] = useState("");
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const taRef = useRef(null);

  useAutosizeTextarea(taRef, text, { min: 140, max: 360 });

  const storagePath = useMemo(() => {
    if (!podcastId || !langCode || !nodeIdStr) return "";
    return `Podcasts/${String(podcastId)}/${String(langCode).toUpperCase()}/${String(nodeIdStr)}/sentences.json`;
  }, [podcastId, langCode, nodeIdStr]);

  const load = async () => {
    if (!storagePath) {
      setExists(false);
      setText("");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setExists(false);
      setText("");

      const url = await getDownloadURL(storageRef(storage, storagePath));
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Failed to fetch sentences.json (${resp.status})`);
      const json = await resp.json();

      setText(safeStringify(json, json));
      setExists(true);
    } catch (err) {
      const code = err?.code || "";
      if (code === "storage/object-not-found") {
        setExists(false);
        setText("");
        return;
      }

      console.error("Failed to load sentences.json:", err);
      setError("Failed to load sentences.json.");
      setExists(false);
      setText("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    load();
  }, [isActive, storagePath]);

  if (!isActive || (!exists && !loading && !error)) return null;

  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontWeight: 800 }}>Transcript sentences</div>
          <div style={{ color: "#4b5563", fontSize: 13, marginTop: 4 }}>{storagePath}</div>
        </div>
        <button onClick={load} disabled={loading || !storagePath}>
          {loading ? "Loading..." : "Reload"}
        </button>
      </div>

      {error && <div style={{ marginTop: 8, color: "crimson", fontWeight: 700 }}>{error}</div>}

      {exists && (
        <textarea
          ref={taRef}
          value={text}
          readOnly
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 140,
            maxHeight: 360,
            marginTop: 8,
            overflowY: "auto",
            resize: "vertical",
            padding: 10,
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "#f9fafb",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            whiteSpace: "pre",
            lineHeight: 1.35
          }}
        />
      )}
    </div>
  );
};



const PostAudioTab = ({ isActive, podcastId, nodeId, selectedLanguage }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);

  const langCode = String(selectedLanguage || "EN").toUpperCase();
  const nodeIdStr = nodeId != null ? String(nodeId) : "";
  const latestRunId = langCode ? `${langCode}_latest` : "";

  const [metaData, setMetaData] = useState(null);
    const [savingRouteTimeVisibility, setSavingRouteTimeVisibility] = useState(false);
    const [routeTimeVisibilityError, setRouteTimeVisibilityError] = useState("");
    const [canonicalUrl, setCanonicalUrl] = useState("");
    const [mediaKind, setMediaKind] = useState("audio");

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [uploadStatus, setUploadStatus] = useState("");
    const [generatingVisuals, setGeneratingVisuals] = useState(false);
    const [generationError, setGenerationError] = useState("");
    const [generationSubmitData, setGenerationSubmitData] = useState(null);
    const [latestRunData, setLatestRunData] = useState(null);
    const [latestRunLoadError, setLatestRunLoadError] = useState("");

    const inferMediaKindFromMetadata = (meta = {}) => {
        const explicit = String(meta.mediaType || meta.kind || "").toLowerCase();
        if (explicit === "video" || explicit.startsWith("video/")) return "video";
        if (explicit === "audio" || explicit.startsWith("audio/")) return "audio";

        const contentType = String(meta.contentType || "").toLowerCase();
        if (contentType.startsWith("video/")) return "video";
        if (contentType.startsWith("audio/")) return "audio";

        const path = String(meta.objectPath || meta.gsUri || meta.fileName || "").toLowerCase();
        if (path.endsWith(".mp4") || path.endsWith(".mov") || path.endsWith(".webm")) return "video";
        return "audio";
      };

      const inferMediaKindFromFile = (file) => {
        const type = String(file?.type || "").toLowerCase();
        const name = String(file?.name || "").toLowerCase();
        if (type.startsWith("video/") || name.endsWith(".mp4")) return "video";
        return "audio";
      };

    const resolveAudioUrlFromMetadata = async (storage, meta) => {
        const objectPath = meta?.objectPath || "";
        const gsUri = meta?.gsUri || "";
      
        if (objectPath) {
          return getDownloadURL(storageRef(storage, objectPath));
        }
      
        if (gsUri && String(gsUri).startsWith("gs://")) {
          const [, bucketAndKey] = String(gsUri).split("gs://");
          const firstSlash = bucketAndKey.indexOf("/");
          const key = firstSlash >= 0 ? bucketAndKey.slice(firstSlash + 1) : "";
          return getDownloadURL(storageRef(storage, key));
        }
      
        return "";
      };
      
      const getCanonicalMediaPath = ({ podcastId, langCode, nodeIdStr, mediaKind = "audio" }) => {
          const lang = String(langCode || "EN").toUpperCase();
          const ext = mediaKind === "video" ? "mp4" : "mp3";
          return `Podcasts/${String(podcastId)}/${lang}/${String(nodeIdStr)}_${lang}.${ext}`;
        };
        
        const fetchBlobFromUrl = async (url) => {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`Failed to fetch audio blob (${res.status})`);
          return await res.blob();
        };
        
        const buildMetadataForCanonical = ({ canonicalPath, mediaKind = "audio", file = null }) => {
          const contentType = file?.type || (mediaKind === "video" ? "video/mp4" : "audio/mpeg");
          return {
            objectPath: canonicalPath,
            gsUri: "",
            mediaType: mediaKind,
            contentType,
            fileName: file?.name || canonicalPath.split("/").pop(),
            updatedAt: new Date().toISOString()
          };
        };


  const routeScenesRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || !langCode) return null;
    return doc(db, "podcasts", String(podcastId), "podcast_details", nodeIdStr, langCode, "ROUTE_SCENES");
  }, [db, podcastId, nodeIdStr, langCode]);

  const routeTimingsRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || !langCode) return null;
    return doc(db, "podcasts", String(podcastId), "podcast_details", nodeIdStr, langCode, "routeAudioTimings");
  }, [db, podcastId, nodeIdStr, langCode]);

  const metadataRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || !langCode) return null;
    return doc(db, "podcasts", String(podcastId), "podcast_details", nodeIdStr, langCode, "podcastMetadata");
  }, [db, podcastId, nodeIdStr, langCode]);

  const latestRunRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || !latestRunId) return null;
    return doc(db, "podcasts", String(podcastId), "podcast_details", nodeIdStr, "runs", latestRunId);
  }, [db, podcastId, nodeIdStr, latestRunId]);

  const [audioUrl, setAudioUrl] = useState("");
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState("");
  const [syncing, setSyncing] = useState(false);
    const [syncMsg, setSyncMsg] = useState("");
    const autoSyncKeyRef = useRef("");

    const loadAudio = async () => {
        if (!metadataRef) return;
        
        try {
            setAudioLoading(true);
            setAudioError("");
            setAudioUrl("");
            setCanonicalUrl("");
            setMetaData(null);
            setMediaKind("audio");
            setSyncMsg("");
        
            const snap = await getDoc(metadataRef);
            if (!snap.exists()) {
            setAudioError("podcastMetadata missing.");
            return;
            }
        
            const meta = snap.data() || {};
            setMetaData(meta);
            const resolvedMediaKind = inferMediaKindFromMetadata(meta);
            setMediaKind(resolvedMediaKind);
        
            const canonicalPath = getCanonicalMediaPath({
              podcastId,
              langCode,
              nodeIdStr,
              mediaKind: resolvedMediaKind
            });
        
            // Try canonical first
            const canUrl = await getDownloadURL(storageRef(storage, canonicalPath)).catch(() => "");
            if (canUrl) setCanonicalUrl(canUrl);
        
            // Try old metadata path
            const url = await resolveAudioUrlFromMetadata(storage, meta).catch(() => "");
            if (url) setAudioUrl(url);
        
            // If nothing exists anywhere, show the upload CTA
            if (!canUrl && !url) {
            setAudioError("No media found for this node/language.");
            return;
            }
        
            // Background auto-canonicalization:
            // - If canonical exists: update metadata to canonical (if needed)
            // - Else if only url exists: copy url -> canonical and update metadata
            await ensureCanonicalInBackground({
            meta,
            nonCanonicalUrl: url,
            canonicalPath,
            canonicalAlreadyExists: !!canUrl
            });
        
            // Reload to reflect new canonical URL after background work
            const canUrl2 = await getDownloadURL(storageRef(storage, canonicalPath)).catch(() => "");
            if (canUrl2) setCanonicalUrl(canUrl2);
        } catch (e) {
            console.error(e);
            setAudioError("Failed to load media / metadata.");
        } finally {
            setAudioLoading(false);
        }
        };

  const updateMetadataToCanonical = async (canonicalPath, { mediaKind = "audio", file = null } = {}) => {
    if (!metadataRef) return;
  
    await setDoc(
      metadataRef,
      buildMetadataForCanonical({ canonicalPath, mediaKind, file }),
      { merge: true }
    );
  };

  const updateRouteTimeVisibility = async (showRouteTimesWhenPaused) => {
    if (!metadataRef) return;

    const previousValue = metaData?.showRouteTimesWhenPaused !== false;
    setMetaData((current) => ({
      ...(current || {}),
      showRouteTimesWhenPaused
    }));
    setSavingRouteTimeVisibility(true);
    setRouteTimeVisibilityError("");

    try {
      await setDoc(metadataRef, { showRouteTimesWhenPaused }, { merge: true });
    } catch (error) {
      console.error(error);
      setMetaData((current) => ({
        ...(current || {}),
        showRouteTimesWhenPaused: previousValue
      }));
      setRouteTimeVisibilityError("Failed to save route-time visibility.");
    } finally {
      setSavingRouteTimeVisibility(false);
    }
  };
  
  const uploadFileToCanonical = async (file) => {
    if (!file) return;
    if (!podcastId || !nodeIdStr || !langCode) return;
  
    try {
      setUploading(true);
      setUploadError("");
      setUploadStatus("");
  
      const uploadMediaKind = inferMediaKindFromFile(file);
      const canonicalPath = getCanonicalMediaPath({
        podcastId,
        langCode,
        nodeIdStr,
        mediaKind: uploadMediaKind
      });
      const destRef = storageRef(storage, canonicalPath);
  
      await uploadBytes(destRef, file, {
        contentType: file.type || (uploadMediaKind === "video" ? "video/mp4" : "audio/mpeg")
      });
  
      await updateMetadataToCanonical(canonicalPath, { mediaKind: uploadMediaKind, file });
  
      setUploadStatus(`Updated ${uploadMediaKind === "video" ? "video" : "audio"} and metadata`);
      await loadAudio();
    } catch (e) {
      console.error(e);
      setUploadError("Failed to upload media and update metadata.");
    } finally {
      setUploading(false);
    }
  };

  const ensureCanonicalInBackground = async ({ meta, nonCanonicalUrl, canonicalPath, canonicalAlreadyExists }) => {
    if (!metadataRef) return;
  
    const key = `${podcastId}|${langCode}|${nodeIdStr}`;
    if (autoSyncKeyRef.current === key) return; // already attempted this session
  
    // If metadata already points to canonical, nothing to do
    const metaObjectPath = String(meta?.objectPath || "");
    const metaGsUri = String(meta?.gsUri || "");
    const metaPointsToCanonical =
      metaObjectPath === canonicalPath ||
      (metaGsUri.startsWith("gs://") && metaGsUri.includes(canonicalPath));
  
    if (metaPointsToCanonical && canonicalAlreadyExists) {
      autoSyncKeyRef.current = key;
      return;
    }
  
    try {
      setSyncing(true);
      setUploadError("");
      setSyncMsg("");
  
      // Case A: canonical file exists -> just update metadata to canonical (no upload)
      if (canonicalAlreadyExists) {
        const resolvedMediaKind = inferMediaKindFromMetadata(meta);
        setSyncMsg("Syncing metadata to canonical media...");
        await updateMetadataToCanonical(canonicalPath, { mediaKind: resolvedMediaKind });
        autoSyncKeyRef.current = key;
        setSyncMsg("Metadata synced.");
        return;
      }
  
      // Case C: nothing exists -> do nothing (UI will show Upload)
      autoSyncKeyRef.current = key;
    } catch (e) {
      console.error(e);
      setUploadError("Auto-sync failed. Please upload an MP3 or MP4.");
    } finally {
      setSyncing(false);
    }
  };
  
  const runStatus = String(latestRunData?.status || "");
  const activeRunStatuses = new Set([
    "Started",
    "Transcribing",
    "Generating Initial Route scenes",
    "Generating final Route Scenes"
  ]);
  const isRunActive = generatingVisuals || activeRunStatuses.has(runStatus);
  
  const generateVisuals = async() => {
    if (!podcastId || !nodeIdStr || !langCode || !latestRunId) return;

    try {
      setGeneratingVisuals(true);
      setGenerationError("");
      setGenerationSubmitData(null);

      const res = await cloudRunStartPodcastTranscribe(podcastId, nodeIdStr, langCode, {
        runId: latestRunId,
        step: 0
      });

      if (!res?.ok) {
        throw new Error(res?.error || "Failed to submit podcast generation job.");
      }

      setGenerationSubmitData(res.data || null);
    } catch (e) {
      console.error(e);
      setGenerationError(String(e?.message || e || "Failed to submit podcast generation job."));
    } finally {
      setGeneratingVisuals(false);
    }
  };
  

  useEffect(() => {
    if (!isActive) return;
    loadAudio();
  }, [isActive, metadataRef?.path]);

  useEffect(() => {
    if (!isActive || !latestRunRef) {
      setLatestRunData(null);
      setLatestRunLoadError("");
      return undefined;
    }

    setLatestRunLoadError("");
    const unsub = onSnapshot(
      latestRunRef,
      (snap) => {
        setLatestRunData(snap.exists() ? snap.data() : null);
      },
      (err) => {
        console.error("Failed to listen to latest podcast run:", err);
        setLatestRunLoadError("Could not load latest run status.");
      }
    );

    return unsub;
  }, [isActive, latestRunRef]);

  if (!isActive) return null;
  const MediaPreviewElement = mediaKind === "video" ? "video" : "audio";

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontWeight: 800 }}>Route media</div>
            <button onClick={loadAudio} disabled={audioLoading || !metadataRef}>
            {audioLoading ? "Loading…" : "Reload"}
            </button>
        </div>

        <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
            {audioError && <div style={{ color: "crimson", fontWeight: 700 }}>{audioError}</div>}

            {canonicalUrl && (
            <div>
                
                <MediaPreviewElement
                  controls
                  src={canonicalUrl}
                  style={{ width: "100%", maxHeight: mediaKind === "video" ? 320 : undefined }}
                />
            </div>
            )}

            {!canonicalUrl && audioUrl && (
            <div>
                <MediaPreviewElement
                  controls
                  src={audioUrl}
                  style={{ width: "100%", maxHeight: mediaKind === "video" ? 320 : undefined }}
                />
                
            </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                <label
                    style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(0,0,0,0.12)",
                    cursor: uploading ? "default" : "pointer",
                    opacity: uploading ? 0.6 : 1
                    }}
                >
                    <span style={{ fontWeight: 800 }}>Upload MP3 or MP4</span>
                    <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,video/mp4"
                    disabled={uploading}
                    onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) uploadFileToCanonical(f);
                        e.target.value = "";
                    }}
                    style={{ display: "none" }}
                    />
                </label>

                {syncing && (
                    <div style={{ fontWeight: 800, opacity: 0.8 }}>
                    {syncMsg || "Syncing…"}
                    </div>
                )}
            </div>

            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                width: "fit-content",
                fontWeight: 700
              }}
            >
              <input
                type="checkbox"
                checked={metaData?.showRouteTimesWhenPaused !== false}
                disabled={!metadataRef || !metaData || savingRouteTimeVisibility}
                onChange={(event) => updateRouteTimeVisibility(event.target.checked)}
              />
              Show route times while audio is paused
              {savingRouteTimeVisibility && <span style={{ fontWeight: 500 }}>Saving…</span>}
            </label>
            {routeTimeVisibilityError && (
              <div style={{ color: "crimson", fontWeight: 700 }}>{routeTimeVisibilityError}</div>
            )}

            

            {(uploadError || uploadStatus || syncMsg) && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {uploadError && <div style={{ color: "crimson", fontWeight: 700 }}>{uploadError}</div>}
                {!uploadError && uploadStatus && <div style={{ color: "#065f46", fontWeight: 700 }}>{uploadStatus}</div>}
                {!uploadError && !uploadStatus && syncMsg && !syncing && (
                <div style={{ color: "#065f46", fontWeight: 700 }}>{syncMsg}</div>
                )}
            </div>
            )}
        </div>
        </div>

        {(canonicalUrl || audioUrl)?
            <div>
                <button onClick={generateVisuals} disabled={isRunActive || !latestRunRef}>
                    {isRunActive ? "Generation running..." : "Generate Visuals"}
                </button>
                <div style={{ marginTop: 8, display: "grid", gap: 4 }}>
                  <div style={{ fontSize: 13, color: "#4b5563" }}>
                    Latest run: <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>{latestRunId || "—"}</span>
                  </div>
                  <div style={{ fontWeight: 800 }}>
                    Status: {runStatus || (generationSubmitData ? "Submitted" : "No run yet")}
                  </div>
                  {generationSubmitData?.jobId && (
                    <div style={{ fontSize: 13, color: "#4b5563" }}>
                      Job: <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>{generationSubmitData.jobId}</span>
                    </div>
                  )}
                  {runStatus === "Complete" && (
                    <div style={{ color: "#065f46", fontWeight: 800 }}>Generation complete. Reload route scenes/timings if needed.</div>
                  )}
                  {runStatus === "Failed" && (
                    <div style={{ color: "crimson", fontWeight: 800 }}>Generation failed.</div>
                  )}
                  {(generationError || latestRunLoadError) && (
                    <div style={{ color: "crimson", fontWeight: 800 }}>{generationError || latestRunLoadError}</div>
                  )}
                </div>
            </div>
            :
            null
        }

      <JsonDocEditor
        title="ROUTE_SCENES (content)"
        docRef={routeScenesRef}
        defaultContent={[]}
        isActive={isActive}
      />

      <JsonDocEditor
        title="routeAudioTimings (content)"
        docRef={routeTimingsRef}
        defaultContent={{}}
        isActive={isActive}
      />

      <SentencesJsonViewer
        isActive={isActive}
        storage={storage}
        podcastId={podcastId}
        langCode={langCode}
        nodeIdStr={nodeIdStr}
      />
    </div>
  );
};

export default PostAudioTab;
